import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';

import NavLink from '../NavLink/navlink-component';
import EventCard from '../Cards/event-card-component';
import EventPreviewModal from '../Modal/event-preview-modal-component';
import { getEventsData } from '../../Public/events-data';
import { getEventsScreenData } from '../../Public/events-screen-data';
import {
    buildMonthMatrix,
    getWeekSegments,
    isSameDay,
    startOfDay,
    parseDate,
    isPastEvent,
} from '../Events/event-date-utils';

// ─── Chevron ────────────────────────────────────────────────────────────────
function Chevron({ dir = 'left', className = '' }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
            <path
                d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            />
        </svg>
    );
}

// ─── A single week row of the month grid (dark theme) ────────────────────────
function WeekRow({ week, events, viewMonth, today, onSelect }) {
    const { segments } = useMemo(() => getWeekSegments(week, events), [week, events]);

    return (
        <div className="relative h-[84px] md:h-[104px]">
            {/* Background cells (borders + dimming for adjacent-month days) */}
            <div className="absolute inset-0 grid grid-cols-7 pointer-events-none">
                {week.map((day, i) => (
                    <div
                        key={i}
                        className={`border-t border-primary-100 ${i > 0 ? 'border-s' : ''} ${
                            day.getMonth() !== viewMonth ? 'bg-primary-50/60' : ''
                        }`}
                    />
                ))}
            </div>

            {/* Content grid: date numbers (row 1) + event bars (rows 2+).
                Fixed row height keeps the whole grid a constant height. */}
            <div className="relative grid grid-cols-7 h-full overflow-hidden">
                {week.map((day, i) => {
                    const isToday = isSameDay(day, today);
                    const dim = day.getMonth() !== viewMonth;
                    return (
                        <div key={i} style={{ gridColumn: i + 1, gridRow: 1 }} className="px-1 md:px-2 pt-1.5 md:pt-2 pb-1">
                            <span
                                className={`inline-flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-full text-xs md:text-sm font-bold font-primary ${
                                    isToday
                                        ? 'bg-secondary-500 text-white'
                                        : dim
                                        ? 'text-neutral-300'
                                        : 'text-primary-500'
                                }`}
                            >
                                {day.getDate()}
                            </span>
                        </div>
                    );
                })}

                {segments.map((seg, idx) => {
                    const { event, startCol, span, isStart, isEnd, lane } = seg;
                    const past = isPastEvent(event);
                    const cover = event.thumbnail || event.image;
                    return (
                        <button
                            key={`${event.id}-${idx}`}
                            type="button"
                            title={event.title}
                            onClick={() => onSelect(event)}
                            style={{ gridColumn: `${startCol + 1} / span ${span}`, gridRow: lane + 2 }}
                            className={`group/bar mx-0.5 md:mx-1 mb-1 flex items-center gap-1.5 h-6 md:h-7 px-1 md:px-1.5 overflow-hidden transition-colors cursor-pointer ${
                                past ? 'bg-primary-100 text-primary-400' : 'bg-secondary-500 text-white hover:bg-secondary-600'
                            } ${isStart ? 'rounded-s-lg ms-0.5 md:ms-1' : 'ms-0'} ${isEnd ? 'rounded-e-lg me-0.5 md:me-1' : 'me-0'}`}
                        >
                            {isStart && cover && (
                                <img
                                    src={cover}
                                    alt=""
                                    aria-hidden="true"
                                    className="hidden md:block w-4 h-4 rounded-full object-cover shrink-0 ms-0.5"
                                    loading="lazy"
                                />
                            )}
                            <span className="truncate text-[9px] md:text-[11px] font-bold font-primary leading-none py-1.5 md:py-2 px-0.5">
                                {!isStart && '↔ '}
                                {event.startTime && isStart && !event.allDay && (
                                    <span className="hidden md:inline opacity-70 me-1">{event.startTime}</span>
                                )}
                                {event.title}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Month grid (dark theme) ──────────────────────────────────────────────────
function MonthGrid({ viewDate, events, weekdays, today, onSelect }) {
    const weeks = useMemo(
        () => buildMonthMatrix(viewDate.getFullYear(), viewDate.getMonth()),
        [viewDate]
    );
    const viewMonth = viewDate.getMonth();

    return (
        <div className="rounded-[24px] border border-primary-100 overflow-hidden bg-white shadow-xl">
            {/* Weekday header */}
            <div className="grid grid-cols-7 bg-primary-50 border-b border-primary-100">
                {weekdays.map((wd, i) => (
                    <div key={i} className="py-2.5 md:py-3 text-center text-[11px] md:text-xs font-bold text-neutral-500 font-primary">
                        {wd}
                    </div>
                ))}
            </div>

            {/* Weeks, re-keyed per month so the new month fades/rises in smoothly.
                Row count is fixed (6), so the container height never changes. */}
            <motion.div
                key={`${viewDate.getFullYear()}-${viewMonth}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
                {weeks.map((week, i) => (
                    <WeekRow key={i} week={week} events={events} viewMonth={viewMonth} today={today} onSelect={onSelect} />
                ))}
            </motion.div>
        </div>
    );
}

// ─── Horizontal card list (all viewports) ────────────────────────────────────
function EventCardList({ events, lang, loading, emptyLabel, grid = false }) {
    // Grid: at least 3 cards side by side on desktop (wraps to more rows).
    // Scroll: single-row horizontal carousel (default, used on the /events page).
    if (loading) {
        return grid ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-[440px] lg:h-[380px] rounded-[28px] bg-white/5 animate-pulse" />
                ))}
            </div>
        ) : (
            <div className="flex gap-4 overflow-hidden">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="flex-none w-[88vw] max-w-[400px] lg:w-[320px] h-[440px] lg:h-[380px] rounded-[28px] bg-white/5 animate-pulse" />
                ))}
            </div>
        );
    }

    if (!events.length) {
        return <p className="text-neutral-400 text-sm py-8 font-primary">{emptyLabel}</p>;
    }

    if (grid) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {events.map((ev) => (
                    <EventCard key={ev.id} event={ev} lang={lang} />
                ))}
            </div>
        );
    }

    return (
        <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 lg:gap-6 pb-4 px-4 lg:px-1 scrollbar-hide">
            {events.map((ev) => (
                <div key={ev.id} className="flex-none w-[88vw] max-w-[400px] lg:w-[320px] snap-start">
                    <EventCard event={ev} lang={lang} />
                </div>
            ))}
        </div>
    );
}

// ─── Main section (dark) ────────────────────────────────────────────────────
export default function EventsCalendarSection({ events: propEvents, title, subtitle, viewAllHref, showPast = false, gridCards = false, combinePast = false, maxCards = null }) {
    const { language = 'he', dir = 'rtl' } = useSelector((s) => s.systemData || {});
    const { calendar: t } = getEventsScreenData(language);
    const isRtl = dir === 'rtl';
    const today = useMemo(() => startOfDay(new Date()), []);

    const [events, setEvents] = useState(propEvents || []);
    const [loading, setLoading] = useState(!propEvents);
    const [viewDate, setViewDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (propEvents) {
            setEvents(propEvents);
            return;
        }
        let alive = true;
        setLoading(true);
        getEventsData(language).then((data) => {
            if (!alive) return;
            setEvents(data || []);
            setLoading(false);
            // Jump the calendar to the month of the next upcoming event.
            const upcomingEvent = (data || []).find((e) => !isPastEvent(e));
            if (upcomingEvent) {
                const d = parseDate(upcomingEvent.startDate);
                if (d) setViewDate(new Date(d.getFullYear(), d.getMonth(), 1));
            }
        });
        return () => { alive = false; };
    }, [language, propEvents]);

    const futureEvents = useMemo(() => events.filter((e) => !isPastEvent(e)), [events]);
    // Past events, most-recent first.
    const pastEvents = useMemo(() => events.filter((e) => isPastEvent(e)).reverse(), [events]);

    // The single card row:
    //  - showPast    → strictly future (past gets its own row below)
    //  - combinePast → one line: upcoming first, then recent past (fill to maxCards)
    //  - otherwise   → future, falling back to most-recent so it's never empty (/events)
    // maxCards caps the row (e.g. 3 side by side on the landing).
    const upcoming = useMemo(() => {
        let list;
        if (showPast) list = futureEvents;
        else if (combinePast) list = [...futureEvents, ...pastEvents];
        else list = futureEvents.length ? futureEvents : [...events].reverse();
        return maxCards ? list.slice(0, maxCards) : list;
    }, [showPast, combinePast, maxCards, futureEvents, pastEvents, events]);

    const goMonth = (delta) =>
        setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));

    const monthLabel = `${t.months[viewDate.getMonth()]} ${viewDate.getFullYear()}`;

    return (
        <>
        <section dir={dir} className="relative w-full overflow-hidden bg-primary-950 py-14 md:py-20 px-4">
            {/* Ambient purple glows, matches the landing hero treatment */}
            <div className="pointer-events-none absolute -top-1/4 right-0 h-[60vh] w-[60vh] rounded-full bg-secondary-700/20 blur-[120px]" />
            <div className="pointer-events-none absolute bottom-0 left-0 h-[45vh] w-[45vh] rounded-full bg-secondary-900/30 blur-[120px]" />

            <div className="relative z-10 max-w-5xl mx-auto">
                {/* Heading */}
                {(title || subtitle) && (
                    <div className={`mb-8 ${isRtl ? 'text-right' : 'text-left'}`}>
                        {title && (
                            <h2 className="font-secondary text-3xl md:text-5xl font-extralight text-white leading-tight">
                                {title}
                            </h2>
                        )}
                        {subtitle && (
                            <p className="mt-3 text-base md:text-lg text-neutral-400 font-primary max-w-2xl">
                                {subtitle}
                            </p>
                        )}
                    </div>
                )}

                {/* ── Card list (all viewports) ── */}
                <div className="mb-14 lg:mb-16">
                    <div className="flex items-center justify-between gap-4 mb-5">
                        <h3 className={`font-secondary text-xl md:text-2xl font-extralight text-white ${isRtl ? 'text-right' : 'text-left'}`}>
                            {t.upcoming}
                        </h3>
                        {viewAllHref && (
                            <NavLink
                                href={viewAllHref}
                                title={t.viewAll}
                                showUnderline={false}
                                preserveStyle
                                className="group flex items-center gap-2 text-sm md:text-base font-bold text-secondary-300 hover:text-white transition-colors font-primary shrink-0 !opacity-100"
                            >
                                <span>{t.viewAll}</span>
                                <span className={`transition-transform duration-200 ${isRtl ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}>
                                    {isRtl ? '←' : '→'}
                                </span>
                            </NavLink>
                        )}
                    </div>
                    <EventCardList events={upcoming} lang={language} loading={loading} emptyLabel={t.noEvents} grid={gridCards} />
                </div>

                {/* ── Past events row (opt-in) ── */}
                {showPast && !loading && pastEvents.length > 0 && (
                    <div className="mb-14 lg:mb-16">
                        <h3 className={`font-secondary text-xl md:text-2xl font-extralight text-white/70 mb-5 ${isRtl ? 'text-right' : 'text-left'}`}>
                            {t.past}
                        </h3>
                        <EventCardList events={pastEvents} lang={language} loading={false} emptyLabel={t.noEvents} grid={gridCards} />
                    </div>
                )}

                {/* ── Month grid (all viewports) ── */}
                <div>
                    <div className="flex items-center justify-between mb-5 md:mb-6">
                        <h3 className="font-secondary text-xl md:text-3xl font-extralight text-white min-w-[150px] md:min-w-[220px]">
                            {monthLabel}
                        </h3>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => goMonth(isRtl ? 1 : -1)}
                                aria-label="previous month"
                                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                            >
                                <Chevron dir={isRtl ? 'right' : 'left'} className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => goMonth(isRtl ? -1 : 1)}
                                aria-label="next month"
                                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                            >
                                <Chevron dir={isRtl ? 'left' : 'right'} className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1))}
                                className="ms-2 px-3 md:px-4 h-9 rounded-full border border-white/15 text-sm font-bold text-white hover:bg-white/10 transition-colors font-primary"
                            >
                                {t.today}
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="h-[420px] md:h-[520px] rounded-[24px] border border-white/10 bg-white/10 animate-pulse" />
                    ) : (
                        <MonthGrid viewDate={viewDate} events={events} weekdays={t.weekdays} today={today} onSelect={setSelected} />
                    )}
                </div>
            </div>
        </section>

        <AnimatePresence>
            {selected && (
                <EventPreviewModal
                    event={selected}
                    lang={language}
                    dir={dir}
                    onClose={() => setSelected(null)}
                />
            )}
        </AnimatePresence>
        </>
    );
}
